import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { X } from "lucide-react";
import { cn } from "utils";

/**
 * Shared dialog shell built on Radix Dialog.
 *
 * - Content only mounts while `open` is true, so heavy children are not rendered
 *   behind a closed dialog.
 * - Menus that open dialogs should use `modal={false}` so two modal scroll-locks
 *   do not stack on the body.
 * - Optional `description` → DialogDescription (aria-describedby). Without it we
 *   set aria-describedby={undefined} so Radix does not warn.
 * - Optional `testId` → data-testid on DialogContent for e2e.
 */
const AppDialog = ({
  open,
  onOpenChange,
  onClose,
  header,
  description,
  descriptionClassName,
  children,
  className,
  contentClassName,
  testId,
  ...props
}) => {
  const handleOpenChange = (nextOpen) => {
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} {...props}>
      {open ? (
        <DialogContent
          className={cn(
            header &&
              "flex max-h-[100vh] flex-col gap-0 overflow-hidden p-0 [&>button.absolute]:hidden",
            className
          )}
          data-testid={testId}
          {...(description ? {} : { "aria-describedby": undefined })}
          onOpenAutoFocus={(event) => {
            const target = event.currentTarget;
            if (!(target instanceof HTMLElement)) {
              return;
            }
            event.preventDefault();
            const focusable = target.querySelector(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusable instanceof HTMLElement) {
              focusable.focus();
            } else {
              target.focus();
            }
          }}
        >
          {header ? (
            <>
              <DialogHeader className="flex shrink-0 flex-row items-center gap-4 space-y-0 border-b border-slate-100 px-6 py-4 text-left sm:text-left">
                <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
                  {typeof header === "string" ? (
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <DialogTitle className="font-inter text-xl font-semibold text-slate-700">
                        {header}
                      </DialogTitle>
                      {description ? (
                        <DialogDescription
                          className={cn(
                            "font-inter text-xs font-normal text-slate-700",
                            descriptionClassName
                          )}
                        >
                          {description}
                        </DialogDescription>
                      ) : null}
                    </div>
                  ) : (
                    header
                  )}
                </div>
                <DialogClose asChild>
                  <button
                    type="button"
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gray-500 text-white transition hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </DialogClose>
              </DialogHeader>
              {typeof header !== "string" && description ? (
                <DialogDescription
                  className={cn("sr-only", descriptionClassName)}
                >
                  {description}
                </DialogDescription>
              ) : null}
              <div className={cn("min-h-0 overflow-auto", contentClassName)}>
                {children}
              </div>
            </>
          ) : (
            <>
              {description ? (
                <DialogDescription
                  className={cn("sr-only", descriptionClassName)}
                >
                  {description}
                </DialogDescription>
              ) : null}
              {children}
            </>
          )}
        </DialogContent>
      ) : null}
    </Dialog>
  );
};

export default AppDialog;
